
















































































import { Scrollbar, SvgImage } from '@/app/components';
import { useQueryParams, useSockets } from '@/app/composable';
import { useFilters } from '@/app/composable/filters';
import { useRouter } from '@/app/composable/router';
import { ExecutionAPI } from '@/modules/workflow-designer/api';
import { ExecutionStatus, ExecutionType } from '@/modules/workflow-designer/constants';
import { useAxios } from '@/app/composable';
import { ExclamationIcon } from '@vue-hero-icons/solid';
import { computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { isNil } from 'ramda';
import { Asset } from '../types';
import { AssetMetrics, ColumnMetrics, CombinedMetrics } from './data-profiling';

dayjs.extend(minMax);

export default defineComponent({
    name: 'DataProfilingTab',
    props: {
        id: {
            type: [Number, String],
            required: true,
        },
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        innerActiveTab: {
            type: String,
            required: false,
            default: '',
        },
    },
    components: {
        Scrollbar,
        SvgImage,
        CombinedMetrics,
        ColumnMetrics,
        AssetMetrics,
        ExclamationIcon,
    },
    setup(props: any, { emit, root }: { emit: any; root: any }) {
        const { fromNow } = useFilters();
        const assetMetricsRef = ref<any>(null);
        const { exec } = useAxios(true);
        const isStreaming = ref<boolean>(false);
        const isExecutionRunning = ref<boolean>(false);
        const router = useRouter();
        const { get, set } = useQueryParams(root, router, 'assets:view');

        const {
            subscribe,
            unsubscribe,
            WebSocketsEvents,
            joinSocketRoom,
            leaveSocketRoom,
            WebSocketsRoomTypes,
        } = useSockets();

        // Inner tabs variables
        const activeTab = ref<string>('asset-metrics');
        const availableTabs: { key: 'asset-metrics' | 'column-metrics' | 'combined-metrics'; title: string }[] = [
            { key: 'asset-metrics', title: 'Asset Metrics' },
            { key: 'column-metrics', title: 'Column Metrics' },
            { key: 'combined-metrics', title: 'Combined Metrics' },
        ];

        const changeTab = (key: string) => {
            if (activeTab.value === key) return;
            activeTab.value = key;
            set('data-profiling-tab', key);
        };

        const setActiveTabOnLoad = () => {
            // Case 1: Refresh from parent tab with innerActiveTab prop
            if (props.innerActiveTab && props.innerActiveTab !== 'asset-metrics') {
                activeTab.value = props.innerActiveTab;
                set('data-profiling-tab', props.innerActiveTab);
                return;
            }

            // Case 2: Page load with query param or browser refresh
            const tabParam = get('data-profiling-tab');
            if (tabParam && availableTabs.find((t) => t.key === tabParam)) {
                activeTab.value = tabParam;
            } else {
                // Case 3: Default
                set('data-profiling-tab', activeTab.value);
            }
        };

        const removeActiveTabParam = () => {
            const tabParam = get('data-profiling-tab');
            if (!isNil(tabParam) && tabParam.length > 0) set('data-profiling-tab', '');
        };

        const metricsAvailable = ref<boolean>(true);
        const metricsLastUpdate = ref<any>(null);

        const loading = computed(() => assetMetricsRef.value?.loading);

        const fetchAssetExecutions = () => {
            exec(ExecutionAPI.getAssetExecutions(+props.id)).then((res: any) => {
                isStreaming.value = !!(res.data?.latestProvenanceExecution?.type === ExecutionType.LongRunning);
                isExecutionRunning.value = !!(res.data?.latestProvenanceExecution?.status === ExecutionStatus.Running);
            });
        };

        const setLastUpdate = (date: string | null) => {
            metricsLastUpdate.value = date;
            metricsAvailable.value = !isNil(date);
        };

        const columns = computed(() => {
            const cols = !isNil(props.asset.structure?.columns) ? props.asset.structure.columns : [];
            return cols.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
        });

        const onMessage = async (message: any) => {
            emit('new-exploratory-metrics', message.refresh.type === 'category1');
        };

        onMounted(() => {
            fetchAssetExecutions();
            subscribe(WebSocketsEvents.ExploratoryMetrics, (msg: any) => onMessage(msg));
            joinSocketRoom(WebSocketsRoomTypes.Asset, props.id);
            setActiveTabOnLoad();
        });

        onBeforeUnmount(() => {
            unsubscribe(WebSocketsEvents.ExploratoryMetrics);
            leaveSocketRoom(WebSocketsRoomTypes.Asset, props.id);
            removeActiveTabParam();
        });

        return {
            isNil,
            dayjs,
            fromNow,
            metricsLastUpdate,
            activeTab,
            availableTabs,
            changeTab,
            metricsAvailable,
            assetMetricsRef,
            loading,
            isStreaming,
            isExecutionRunning,
            columns,
            setLastUpdate,
        };
    },
});
